/**
 * Shared per-tenant actions menu.
 *
 * Extracted so the Room-detail "Current tenant" card (and any future surface —
 * e.g. the Tenants list row) render the identical action set and don't drift.
 * The caller owns the mutations; this component is presentation + wiring only.
 */
import { Link } from "react-router-dom";
import {
  MoreHorizontal,
  Eye,
  LogIn,
  Replace,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function TenantActionsMenu({
  profileUrl,
  isSuperAdmin,
  canActAs,
  archived,
  busy = false,
  onActAsTenant,
  onReassign,
  onToggleArchive,
}: {
  profileUrl: string;
  /** Gates "Reassign room…" — a super-admin-only booking move. */
  isSuperAdmin: boolean;
  /** Admin or super_admin — gates "Act as tenant". */
  canActAs: boolean;
  archived: boolean;
  busy?: boolean;
  /** Always available; the caller decides what to do when there's no account. */
  onActAsTenant: () => void;
  onReassign: () => void;
  onToggleArchive: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Tenant actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem asChild>
          <Link to={profileUrl}>
            <Eye className="mr-2 h-4 w-4" />
            View full profile
          </Link>
        </DropdownMenuItem>

        {canActAs && (
          <DropdownMenuItem onClick={onActAsTenant}>
            <LogIn className="mr-2 h-4 w-4" />
            Act as tenant
          </DropdownMenuItem>
        )}

        {isSuperAdmin && !archived && (
          <DropdownMenuItem onClick={onReassign}>
            <Replace className="mr-2 h-4 w-4" />
            Reassign room…
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={onToggleArchive}
          disabled={busy}
          className={archived ? "" : "text-muted-foreground"}
        >
          {archived ? (
            <>
              <ArchiveRestore className="mr-2 h-4 w-4" />
              Unarchive
            </>
          ) : (
            <>
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
